import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SidebarThemeSettings from "../components/SidebarThemeSettings";
import VaultPanel from "../components/vault/VaultPanel";
import ConfigsPanel from "../components/vault/ConfigsPanel";
import axiosInstance from "../components/api/axiosInstance";
import { updateClientPortalProfile } from "../utils/authStorage";
import { getPortalSession, getSettingsAccess } from "./portalSession";
import "../assets/css/Settings.css";

const TAB_THEMES = "themes";
const TAB_CONFIGS = "configs";
const TAB_VAULT = "vault";
const TAB_ALIASES = { email: TAB_CONFIGS };

async function saveSidebarThemeToAccount(theme) {
  updateClientPortalProfile({ ...getPortalSession(), Sidebar_Theme: theme });
  try {
    await axiosInstance.put("/client/me", { Sidebar_Theme: theme });
  } catch (_) {
    /* still applied on this device */
  }
}

function TenantSettings() {
  const [access, setAccess] = useState(() => getSettingsAccess());
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let active = true;
    axiosInstance
      .get("/client/me")
      .then((res) => {
        const fresh = res.data?.data;
        if (!active || !fresh?.Settings_Access) return;
        updateClientPortalProfile({ ...getPortalSession(), Settings_Access: fresh.Settings_Access });
        setAccess(getSettingsAccess(fresh));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const tabs = [
    access.email ? { key: TAB_CONFIGS, label: "Configs" } : null,
    access.vault ? { key: TAB_VAULT, label: "Vault" } : null,
    { key: TAB_THEMES, label: "Themes" },
  ].filter(Boolean);
  const rawTab = searchParams.get("tab");
  const requested = TAB_ALIASES[rawTab] || rawTab;
  const tab = tabs.some((t) => t.key === requested) ? requested : tabs[0].key;
  const changeTab = (next, extra = {}) =>
    setSearchParams(next === tabs[0].key ? extra : { tab: next, ...extra }, { replace: true });
  const openInVault = (vaultId) => changeTab(TAB_VAULT, vaultId ? { item: String(vaultId) } : {});

  return (
    <div className="cportal-page">
      {tabs.length > 1 ? (
        <div className="cportal-tabs" role="tablist" aria-label="Settings">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`cportal-tab${tab === t.key ? " active" : ""}`}
              onClick={() => changeTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : null}

      {tab === TAB_THEMES ? (
        <div className="settings-card">
          <SidebarThemeSettings scope="portal" onChange={saveSidebarThemeToAccount} />
        </div>
      ) : null}

      {tab === TAB_CONFIGS ? (
        <div className="settings-card settings-card--wide">
          <ConfigsPanel onManageVault={access.vault ? openInVault : null} />
        </div>
      ) : null}

      {tab === TAB_VAULT ? (
        <div className="settings-card settings-card--wide">
          <VaultPanel
            key={TAB_VAULT}
            basePath="/client/vault"
            changePasswordHref="/portal/profile/change-password"
            title={null}
            emptyText="Nothing here yet. Add email accounts (SMTP / IMAP), API keys, logins or other secrets; values are encrypted before saving."
            description="Email accounts, API keys, logins and other secrets for your company. Values are encrypted and open only with your own login password. Tumma staff cannot view them."
            emailActions={!access.email}
            emailNote="Choose the sending account and test it in Configs."
            focusId={searchParams.get("item")}
          />
        </div>
      ) : null}
    </div>
  );
}

export default TenantSettings;
